import React, { useEffect, useMemo } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Tooltip } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import TablePaginationBar from '../../../../../components/TablePaginationBar';

/** 展开日期列后首列用固定像素；若用「表宽的 28%」，表因 scroll.x 变宽时首列会同比变宽 */
const FIRST_COL_WIDTH_PX_WHEN_DATES_EXPANDED = 280;

export interface DeliveryCompletedSummaryRecord {
    id?: number;
    deliveredDate?: string | null;
    hubId?: number | null;
    hubName?: string | null;
    warehouseId?: number | null;
    warehouseName?: string | null;
    oneSortingCode?: string | null;
    twoSortingCode?: string | null;
    threeSortingCode?: string | null;
    areaId?: number | null;
    areaName?: string | null;
    dspId?: number | null;
    dspName?: string | null;
    dspFullName?: string | null;
    dspContact?: string | null;
    driverId?: number | null;
    driverName?: string | null;
    driverPhone?: string | null;
    deliveredCount: number;
    standardRateDeliveredCount: number;
    discountRateDeliveredCount: number;
    drillLevel?: 'driver' | 'route';
    drillMetric?: 'delivered' | 'standard' | 'discount';
}

interface Props {
    data: DeliveryCompletedSummaryRecord[];
    loading: boolean;
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number, size?: number) => void;
    onDeliveredCountClick?: (record: DeliveryCompletedSummaryRecord) => void;
}

const DeliveryCompletedSummaryTable: React.FC<Props> = ({
    data,
    loading,
    page,
    pageSize,
    total,
    onPageChange,
    onDeliveredCountClick
}) => {
    const { t } = useTranslation();
    const [dateColumnsExpanded, setDateColumnsExpanded] = React.useState<{
        delivered: boolean;
        standard: boolean;
        discount: boolean;
    }>({
        delivered: false,
        standard: false,
        discount: false,
    });
    const [expandedRowKeys, setExpandedRowKeys] = React.useState<React.Key[]>([]);

    const dateKeys = useMemo(() => {
        const set = new Set<string>();
        data.forEach((r) => {
            if (r.deliveredDate) set.add(dayjs(r.deliveredDate).format('YYYY-MM-DD'));
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [data]);

    const anyDatesExpanded =
        dateColumnsExpanded.delivered ||
        dateColumnsExpanded.standard ||
        dateColumnsExpanded.discount;

    /** 展开后：表最小宽 = 首列固定像素 + 右侧各段像素之和（首列不再跟表总宽按比例放大） */
    const horizontalScrollMinWidth = useMemo(() => {
        if (!anyDatesExpanded) return undefined;
        const W_SUB = 112;
        const W_DATE = 88;
        const sectionW = (expanded: boolean) => W_SUB + (expanded ? dateKeys.length * W_DATE : 0);
        const R =
            sectionW(dateColumnsExpanded.delivered) +
            sectionW(dateColumnsExpanded.standard) +
            sectionW(dateColumnsExpanded.discount);
        return FIRST_COL_WIDTH_PX_WHEN_DATES_EXPANDED + R;
    }, [
        anyDatesExpanded,
        dateKeys.length,
        dateColumnsExpanded.delivered,
        dateColumnsExpanded.standard,
        dateColumnsExpanded.discount,
    ]);

    type PivotRow = {
        key: string;
        metricName: string; // 司机名 / 线路名 / 合计
        driverId?: number | null;
        driverName?: string | null;
        driverPhone?: string | null;
        hubId?: number | null;
        warehouseId?: number | null;
        areaId?: number | null;
        areaName?: string | null;
        // 动态字段：delivered_total / delivered_YYYY-MM-DD 等
        [k: string]: any;
        _rawAnyRecord?: DeliveryCompletedSummaryRecord;
        children?: PivotRow[];
    };

    const pivotRows: PivotRow[] = useMemo(() => {
        const driverMap = new Map<string, PivotRow>();

        const safeStr = (v: any) => (v === null || v === undefined ? '' : String(v));
        const getDriverKey = (r: DeliveryCompletedSummaryRecord) => safeStr(r.driverId) || `${safeStr(r.driverName)}_${safeStr(r.driverPhone)}`;
        const getRouteKey = (r: DeliveryCompletedSummaryRecord) =>
            `${safeStr(r.hubId)}_${safeStr(r.warehouseId)}_${safeStr(r.areaId)}_${safeStr(r.areaName)}`;

        const addToRow = (row: PivotRow, r: DeliveryCompletedSummaryRecord) => {
            const deliveredDate = r.deliveredDate ? dayjs(r.deliveredDate).format('YYYY-MM-DD') : '';
            const deliveredKey = `delivered_${deliveredDate}`;
            const standardKey = `standard_${deliveredDate}`;
            const discountKey = `discount_${deliveredDate}`;

            const delivered = Number((r as any).deliveredCount ?? 0) || 0;
            const standard = Number((r as any).standardRateDeliveredCount ?? 0) || 0;
            const discount = Number((r as any).discountRateDeliveredCount ?? 0) || 0;

            row[deliveredKey] = Number(row[deliveredKey] ?? 0) + delivered;
            row[standardKey] = Number(row[standardKey] ?? 0) + standard;
            row[discountKey] = Number(row[discountKey] ?? 0) + discount;

            row.delivered_total = Number(row.delivered_total ?? 0) + delivered;
            row.standard_total = Number(row.standard_total ?? 0) + standard;
            row.discount_total = Number(row.discount_total ?? 0) + discount;
        };

        data.forEach((r) => {
            // 司机行
            const driverKey = getDriverKey(r);
            if (!driverMap.has(driverKey)) {
                const driverLabel = r.driverName || '-';
                driverMap.set(driverKey, {
                    key: `driver_${driverKey}`,
                    metricName: driverLabel,
                    driverId: r.driverId ?? null,
                    driverName: r.driverName ?? null,
                    driverPhone: r.driverPhone ?? null,
                    delivered_total: 0,
                    standard_total: 0,
                    discount_total: 0,
                    children: [],
                    _rawAnyRecord: r,
                });
            }
            const driverRow = driverMap.get(driverKey)!;
            addToRow(driverRow, r);

            // 线路行（用 HUB/WHS/AREA 组合 + 名称，作为路线标识）
            const routeKey = getRouteKey(r);
            const children = driverRow.children!;
            let routeRow = children.find((c) => c.key === `route_${driverKey}_${routeKey}`);
            if (!routeRow) {
                const routeLabel = r.areaName || `${r.threeSortingCode || r.areaId || '-'}`;
                routeRow = {
                    key: `route_${driverKey}_${routeKey}`,
                    metricName: routeLabel,
                    driverId: driverRow.driverId ?? null,
                    driverName: driverRow.driverName ?? null,
                    driverPhone: driverRow.driverPhone ?? null,
                    hubId: r.hubId ?? null,
                    warehouseId: r.warehouseId ?? null,
                    areaId: r.areaId ?? null,
                    areaName: r.areaName ?? null,
                    delivered_total: 0,
                    standard_total: 0,
                    discount_total: 0,
                };
                children.push(routeRow);
            }
            addToRow(routeRow, r);
        });

        // 排序：司机按妥投量（合计）倒序，线路按妥投量（合计）倒序
        const driverRows = Array.from(driverMap.values()).sort((a, b) => {
            const diff = Number(b.delivered_total || 0) - Number(a.delivered_total || 0);
            if (diff !== 0) return diff;
            return a.metricName.localeCompare(b.metricName);
        });
        driverRows.forEach((d) => {
            if (d.children) {
                d.children.sort((a, b) => {
                    const diff = Number(b.delivered_total || 0) - Number(a.delivered_total || 0);
                    if (diff !== 0) return diff;
                    return a.metricName.localeCompare(b.metricName);
                });
            }
        });

        return driverRows;
    }, [data]);

    // 默认收起司机行；数据/翻页变化时重置为全部收起，避免旧 key 残留
    useEffect(() => {
        setExpandedRowKeys([]);
    }, [pivotRows]);

    const renderMetricCell = (
        value: any,
        row: PivotRow,
        metric: 'delivered' | 'standard' | 'discount',
        deliveredDate?: string
    ) => {
        const v = Number(value || 0);
        if (!onDeliveredCountClick || v <= 0) {
            return <span style={{ whiteSpace: 'nowrap' }}>{v}</span>;
        }

        const drillRecord: DeliveryCompletedSummaryRecord = {
            deliveredDate: deliveredDate ?? null,
            hubId: row.hubId ?? null,
            warehouseId: row.warehouseId ?? null,
            areaId: row.areaId ?? null,
            areaName: row.areaName ?? null,
            driverId: row.driverId ?? null,
            driverName: row.driverName ?? null,
            driverPhone: row.driverPhone ?? null,
            drillLevel: row.areaId != null || row.hubId != null || row.warehouseId != null ? 'route' : 'driver',
            drillMetric: metric,
            // 下面这些字段对抽屉不重要，但保持类型完整
            deliveredCount: 0,
            standardRateDeliveredCount: 0,
            discountRateDeliveredCount: 0,
        };

        // 线路行：用 HUB/WHS/AREA 维度下钻更精确
        // 司机汇总行：保持为“全线路”，不要从 _rawAnyRecord 回填 route，否则标题/过滤会错误地变成某一条线路
        if (drillRecord.drillLevel === 'route' && row._rawAnyRecord) {
            drillRecord.hubId = drillRecord.hubId ?? row._rawAnyRecord.hubId ?? null;
            drillRecord.warehouseId = drillRecord.warehouseId ?? row._rawAnyRecord.warehouseId ?? null;
            drillRecord.areaId = drillRecord.areaId ?? row._rawAnyRecord.areaId ?? null;
            drillRecord.areaName = drillRecord.areaName ?? row._rawAnyRecord.areaName ?? null;
            drillRecord.driverId = drillRecord.driverId ?? row._rawAnyRecord.driverId ?? null;
            drillRecord.driverName = drillRecord.driverName ?? row._rawAnyRecord.driverName ?? null;
            drillRecord.driverPhone = drillRecord.driverPhone ?? row._rawAnyRecord.driverPhone ?? null;
        }

        // drillMetric 会传给明细抽屉：delivered → 不筛订单费率；standard → 仅标准；discount → 仅折扣（见 buildDetailFilter 的 isHalfPriceOrder）
        return (
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDeliveredCountClick(drillRecord);
                }}
                style={{ color: '#1890ff' }}
                className="cursor-pointer"
            >
                <span style={{ whiteSpace: 'nowrap' }}>{v}</span>
            </a>
        );
    };

    const metricGroupColumns = useMemo(() => {
        const getToggleDateColumnsNode = (metricKey: keyof typeof dateColumnsExpanded) => (
            <Tooltip title={dateColumnsExpanded[metricKey] ? t('wms.delivery.deliveryCompletedSummary.pivot.collapseDateColumns') : t('wms.delivery.deliveryCompletedSummary.pivot.expandDateColumns')}>
                <span
                    className="cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        setDateColumnsExpanded((prev) => ({
                            ...prev,
                            [metricKey]: !prev[metricKey],
                        }));
                    }}
                >
                    {dateColumnsExpanded[metricKey] ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                </span>
            </Tooltip>
        );

        const dateChildren = (prefix: 'delivered' | 'standard' | 'discount') => {
            const metricKey = prefix as keyof typeof dateColumnsExpanded;
            const subtotalWidth = anyDatesExpanded ? 112 : ('24%' as const);
            const dateColWidth = 88;
            const children: ColumnsType<PivotRow> = [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1 flex-nowrap whitespace-nowrap">
                            <span>{t('wms.delivery.deliveryCompletedSummary.pivot.subtotal')}</span>
                            {getToggleDateColumnsNode(metricKey)}
                        </div>
                    ),
                    dataIndex: `${prefix}_total`,
                    key: `${prefix}_total`,
                    width: subtotalWidth,
                    align: 'center',
                    ellipsis: false,
                    render: (val: any, row: PivotRow) => renderMetricCell(val, row, prefix),
                },
                ...(dateColumnsExpanded[metricKey]
                    ? dateKeys.map((d) => ({
                        title: dayjs(d).format('MM-DD'),
                        dataIndex: `${prefix}_${d}`,
                        key: `${prefix}_${d}`,
                        width: dateColWidth,
                        align: 'center' as const,
                        ellipsis: false,
                        render: (val: any, row: PivotRow) => renderMetricCell(val, row, prefix, d),
                    }))
                    : []),
            ];
            return children;
        };

        return [
            {
                title: t('wms.delivery.deliveryCompletedSummary.pivot.deliveredPackages'),
                key: 'delivered_group',
                align: 'center',
                children: dateChildren('delivered'),
            },
            {
                title: t('wms.delivery.deliveryCompletedSummary.pivot.standardRateDeliveredPackages'),
                key: 'standard_group',
                align: 'center',
                children: dateChildren('standard'),
            },
            {
                title: t('wms.delivery.deliveryCompletedSummary.pivot.discountRateDeliveredPackages'),
                key: 'discount_group',
                align: 'center',
                children: dateChildren('discount'),
            },
        ] as any;
    }, [dateKeys, t, onDeliveredCountClick, dateColumnsExpanded, anyDatesExpanded]);

    const allColumns: ColumnsType<PivotRow> = useMemo(() => {
        const base: ColumnsType<PivotRow> = [
            {
                title: t('wms.delivery.deliveryCompletedSummary.pivot.driverAndRoute'),
                dataIndex: 'metricName',
                key: 'metricName',
                width: anyDatesExpanded ? FIRST_COL_WIDTH_PX_WHEN_DATES_EXPANDED : ('28%' as const),
                fixed: 'left',
                align: 'left',
                className: 'delivery-completed-summary-name-col',
                ellipsis: false,
                render: (text: string) => {
                    const s = text || '-';
                    return (
                        <span
                            style={{
                                display: 'block',
                                width: '100%',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                                overflow: 'visible',
                            }}
                        >
                            {s}
                        </span>
                    );
                },
            },
        ];

        return [...base, ...(metricGroupColumns as any)];
    }, [metricGroupColumns, t, anyDatesExpanded]);

    const tableScrollY = useMemo(() => {
        // SSR/测试环境兜底：按常见视窗高度估算（约等于 80%）
        if (typeof window === 'undefined') return Math.floor(900 * 0.8);
        // 让表格主体独立滚动，表头吸顶（两层表头会一起 sticky）
        // 用视窗高度比例（约 80%）适配不同分辨率，避免写死固定 px
        const y = Math.floor(window.innerHeight * 0.8);
        const minY = Math.floor(window.innerHeight * 0.55);
        return Math.max(minY, y);
    }, []);

    return (
        <>
            <style>{`
                .delivery-completed-summary-driver-pivot-wrap {
                    width: 100%;
                    max-width: 100%;
                }
                .delivery-completed-summary-driver-pivot .ant-table-wrapper,
                .delivery-completed-summary-driver-pivot .ant-spin-nested-loading,
                .delivery-completed-summary-driver-pivot .ant-spin-container,
                .delivery-completed-summary-driver-pivot .ant-table {
                    width: 100%;
                    max-width: 100%;
                }
                .delivery-completed-summary-driver-pivot .ant-table-thead > tr > th {
                    white-space: nowrap !important;
                    word-break: keep-all;
                    line-height: 1.4;
                    text-align: center;
                    vertical-align: middle;
                    background: #fafafa;
                    overflow: hidden;
                    text-overflow: clip;
                }
                .delivery-completed-summary-driver-pivot .ant-table-thead > tr > th.delivery-completed-summary-name-col {
                    text-align: left;
                    white-space: normal !important;
                    word-break: break-word;
                    overflow-wrap: anywhere;
                    overflow: visible !important;
                    text-overflow: unset;
                }
                .delivery-completed-summary-driver-pivot .ant-table-tbody > tr > td {
                    white-space: nowrap !important;
                    overflow: hidden;
                    text-overflow: clip;
                }
                .delivery-completed-summary-driver-pivot .ant-table-tbody > tr > td.delivery-completed-summary-name-col {
                    white-space: normal !important;
                    word-break: break-word;
                    overflow-wrap: anywhere;
                    overflow: visible !important;
                    text-overflow: unset;
                    vertical-align: top;
                }
                .delivery-completed-summary-driver-pivot .ant-table-thead > tr > th.ant-table-cell-fix-left,
                .delivery-completed-summary-driver-pivot .ant-table-tbody > tr > td.ant-table-cell-fix-left {
                    z-index: 2;
                }
                .delivery-completed-summary-driver-pivot .ant-table-thead > tr > th.ant-table-cell-fix-left {
                    background: #fafafa;
                }
                .delivery-completed-summary-driver-pivot .ant-table-tbody > tr > td.ant-table-cell-fix-left {
                    background: #fff;
                }
                .delivery-completed-summary-driver-pivot .ant-table-tbody > tr.ant-table-row:hover > td.ant-table-cell-fix-left {
                    background: #fafafa;
                }
                /* 参照 dashboard/delivery：表头分隔线为完整灰色实线 */
                .delivery-completed-summary-driver-pivot .ant-table-thead > tr > th:not(:last-child) {
                    border-right: 1px solid #f0f0f0;
                }
                .delivery-completed-summary-driver-pivot .ant-table-thead > tr > th:not(:last-child)::before {
                    display: none;
                }
                .delivery-completed-summary-driver-pivot .ant-table-thead > tr:last-child > th {
                    border-bottom: 1px solid #f0f0f0;
                }
            `}</style>
            <div className="delivery-completed-summary-driver-pivot-wrap">
            <Table
                columns={allColumns}
                dataSource={pivotRows}
                loading={loading}
                rowKey="key"
                pagination={false}
                scroll={{
                    y: tableScrollY,
                    ...(typeof horizontalScrollMinWidth === 'number' ? { x: horizontalScrollMinWidth } : {}),
                }}
                sticky
                size="middle"
                tableLayout="fixed"
                className="mb-4 delivery-completed-summary-driver-pivot"
                expandable={{
                    expandedRowKeys,
                    onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
                    expandRowByClick: false,
                    rowExpandable: (record: PivotRow) => !!record.children && record.children.length > 0,
                    // 参照 dashboard/delivery：不要单独的“展开图标空白列”，把展开图标放回第一列
                    expandIconColumnIndex: 0,
                }}
            />
            </div>
            <TablePaginationBar
                total={total}
                current={page}
                pageSize={pageSize}
                onChange={(newPage, newPageSize) => onPageChange(newPage, newPageSize)}
            />
        </>
    );
};

export default DeliveryCompletedSummaryTable;


